const STORAGE_PREFIX = 'youtube-progress-v1:';
export const YOUTUBE_PROGRESS_EVENT = 'podeux:youtube-progress';
const MAX_VIDEOS = 100;
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_TIME_SECONDS = 2147483647;
const validVideoId = (value) => typeof value === 'string' && /^[A-Za-z0-9_-]{11}$/.test(value);
const storageKey = (userId) => `${STORAGE_PREFIX}${userId ? `user:${encodeURIComponent(userId)}` : 'guest'}`;

const prune = (entries, now) => Object.fromEntries(Object.entries(entries)
    .filter(([videoId, entry]) => validVideoId(videoId)
        && Number.isFinite(entry?.seconds) && entry.seconds > 0
        && Number.isFinite(entry.duration) && entry.duration >= entry.seconds && entry.duration <= MAX_TIME_SECONDS
        && Number.isFinite(entry.updatedAt) && entry.updatedAt <= now
        && entry.updatedAt > now - MAX_AGE_MS)
    .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_VIDEOS));

const readProgress = (userId, now) => {
    try {
        const entries = JSON.parse(window.localStorage.getItem(storageKey(userId)));
        return entries && typeof entries === 'object' && !Array.isArray(entries) ? prune(entries, now) : {};
    } catch {
        return {};
    }
};

const writeProgress = (userId, entries) => {
    try {
        if (Object.keys(entries).length) {
            window.localStorage.setItem(storageKey(userId), JSON.stringify(entries));
        } else {
            window.localStorage.removeItem(storageKey(userId));
        }
    } catch {
        // Playback still works when the browser blocks storage or runs out of space.
    }
};

export const getYouTubeProgressEntry = (userId, videoId, now = Date.now()) => {
    const entries = readProgress(userId, now);
    writeProgress(userId, entries);
    return validVideoId(videoId) ? entries[videoId] || null : null;
};

export const getYouTubeProgress = (userId, videoId, now = Date.now()) =>
    getYouTubeProgressEntry(userId, videoId, now)?.seconds || 0;

export const saveYouTubeProgress = (userId, videoId, seconds, duration, now = Date.now()) => {
    if (!validVideoId(videoId) || !Number.isFinite(seconds) || seconds < 0
        || !Number.isFinite(duration) || duration <= 0 || duration > MAX_TIME_SECONDS || seconds > duration) return false;
    const entries = readProgress(userId, now);
    if (seconds === 0) delete entries[videoId];
    else entries[videoId] = { seconds, duration, updatedAt: now };
    writeProgress(userId, prune(entries, now));
    window.dispatchEvent(new Event(YOUTUBE_PROGRESS_EVENT));
    return true;
};

export const clearYouTubeProgress = (userId, videoId, now = Date.now()) => {
    const entries = readProgress(userId, now);
    delete entries[videoId];
    writeProgress(userId, entries);
    window.dispatchEvent(new Event(YOUTUBE_PROGRESS_EVENT));
};
